import { getFirestore, doc, updateDoc, serverTimestamp, writeBatch, collection } from "firebase/firestore";
import { app } from "../../../firebase-config.js";
import { MediaService } from "../../../services/media.service.js";
import { WebnovelAdapter } from "../../../adapters/webnovel.adapter.js";

import { WebnovelSidebar } from "./WebnovelSidebar.js";
import { WebnovelEditorArea } from "./WebnovelEditorArea.js";
import { WebnovelPreview } from "./WebnovelPreview.js";

const db = getFirestore(app);

export class WebnovelStudio {
    constructor(container, id, rawData, subEpisodes = []) {
        this.container = container;
        this.seriesId = id;

        // 1. Adapter: Normalize Data
        this.state = WebnovelAdapter.toDomain(rawData);

        // [MIGRATION] Override with Subcollection Data (Source of Truth)
        if (subEpisodes && subEpisodes.length > 0) {
            console.log("[WebnovelStudio] Initializing with Subcollection Data:", subEpisodes.length);
            // Sort by Index to ensure correct order
            const sorted = [...subEpisodes].sort((a, b) => (a.index || 0) - (b.index || 0));
            this.state.chapters = sorted.map(WebnovelAdapter.normalizeChapter);
        }

        // Internal UI State
        this.activeIndex = 0;
        this.autoSaveTimer = null; // [NEW] Auto-save timer

        // Components
        this.sidebar = null;
        this.editor = null;
        this.preview = null;
    }

    render() {
        this.container.innerHTML = `
        <div class="w-full h-full flex flex-col bg-zinc-900 text-white font-sans relative overflow-hidden">
             <!-- Background -->
            <div class="absolute inset-0 bg-cover bg-center pointer-events-none opacity-30" style="background-image: url('/login.webp');"></div>
            <div class="absolute inset-0 bg-zinc-900/90 backdrop-blur-md z-0"></div>

            <!-- Header -->
             <div class="relative z-20 h-14 bg-black/40 border-b border-white/5 flex items-center justify-between px-4 flex-shrink-0 backdrop-blur-md">
                <div class="flex items-center gap-3">
                    <button id="btn-back" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
                        <span class="material-symbols-outlined">arrow_back</span>
                    </button>
                    <h1 class="font-bold text-white tracking-wide text-sm md:text-base">Novel Studio <span class="text-xs text-zinc-500 font-normal ml-2">v2.1 (Workflow)</span></h1>
                </div>
                
                <div class="flex items-center gap-2">
                     <!-- Auto Save Indicator -->
                     <span class="text-xs text-zinc-500 mr-2 opacity-0 transition-opacity font-bold flex items-center gap-1" id="auto-save-indicator">
                        <span class="material-symbols-outlined text-[14px]">cloud_done</span> 저장됨
                     </span>
                     
                     <div class="h-4 w-px bg-white/10 mx-2"></div>

                     <!-- [AI] Analysis Button (Admin Only) -->
                     ${(window.user?.role === 'admin' || window.user?.isSuperAdmin) ? `
                     <button id="btn-analyze" class="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg border border-purple-400/20 shadow-lg shadow-purple-900/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2" title="AI SEO 분석 (관리자 전용)">
                        <span class="material-symbols-outlined text-[14px]">psychology</span> <span class="hidden md:inline">AI Analyze</span>
                    </button>` : ''}

                     <!-- Workflow Buttons -->
                     <button id="btn-save" class="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold rounded-lg border border-white/10 transition-all active:scale-[0.98]" title="${window.locale.t('studio.save_preserve_status')}">
                        ${window.locale.t('editor.save')}
                     </button>

                     <button id="btn-draft" class="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-500 hover:text-amber-400 text-xs font-bold rounded-lg border border-amber-500/20 hover:border-amber-500/50 transition-all active:scale-[0.98]">
                        <span class="flex items-center gap-1"><span class="material-symbols-outlined text-[14px]">lock</span> ${window.locale.t('editor.save_draft')}</span>
                     </button>

                     <button id="btn-publish" class="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg shadow-lg shadow-rose-900/20 flex items-center justify-center gap-1 transition-all active:scale-[0.98]">
                        <span class="material-symbols-outlined text-[14px]">rocket_launch</span> ${window.locale.t('editor.publish_now')}
                     </button>
                </div>
            </div>

            <!-- 3-Column Layout -->
            <div class="relative z-10 w-full flex-grow flex overflow-hidden">
                <!-- 1. Left Sidebar -->
                <div id="studio-sidebar" class="h-full"></div>

                <!-- 2. Center Editor -->
                <main class="flex-grow flex flex-col bg-zinc-900/50 border-r border-white/10 min-w-0">
                    <div class="flex-grow overflow-y-auto custom-scrollbar p-6 md:p-10" id="editor-area"></div>
                </main>

                <!-- 3. Right Preview -->
                <aside class="w-[400px] flex-shrink-0 flex flex-col bg-black h-full hidden xl:flex border-l border-white/10">
                     <div class="h-10 bg-zinc-950 border-b border-white/5 flex items-center justify-between px-4">
                        <div class="flex items-center gap-3">
                            <button id="btn-refresh-preview" class="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-300 rounded border border-white/10 flex items-center gap-1 transition-all">
                                <span class="material-symbols-outlined text-[14px]">refresh</span> 새로고침
                            </button>
                            <span class="text-[10px] font-bold text-zinc-500 uppercase flex items-center gap-1">
                                <div id="preview-status-dot" class="w-1.5 h-1.5 rounded-full bg-green-500 transition-colors"></div>
                                <span id="preview-status-text">Live Preview</span>
                            </span>
                        </div>
                    </div>
                    <div class="flex-grow overflow-y-auto custom-scrollbar bg-black" id="wn-preview-container"></div>
                </aside>
            </div>
        </div>
        `;

        this.bindGlobalEvents();
        this.mountComponents();
    }

    mountComponents() {
        // 1. Sidebar
        this.sidebar = new WebnovelSidebar(this.container.querySelector('#studio-sidebar'), {
            data: this.state,
            activeIndex: this.activeIndex,
            onSelectChapter: (idx) => {
                this.activeIndex = idx;
                this.refreshUI();
            },
            onAddChapter: () => {
                const newChap = WebnovelAdapter.createEmptyChapter();
                newChap.title = `Episode ${this.state.chapters.length + 1}`;
                this.state.chapters.push(newChap);
                this.activeIndex = this.state.chapters.length - 1;
                this.refreshUI();
            },
            onDeleteChapter: async (idx) => {
                if (await window.Modal.confirm("정말 이 챕터를 삭제하시겠습니까? (복구 불가)", "영구 삭제 확인")) {
                    const chapter = this.state.chapters[idx];

                    // [FIX] Real DB Deletion
                    if (chapter.id) {
                        const { DaVinciService } = await import("../../../lab/davinci/DaVinciService.js");
                        const res = await DaVinciService.deleteEpisode(this.seriesId, chapter.id);
                        if (!res.success) {
                            window.Modal.alert("삭제 실패: " + res.error);
                            return;
                        }
                    }

                    this.state.chapters.splice(idx, 1);
                    if (this.activeIndex >= this.state.chapters.length) {
                        this.activeIndex = Math.max(0, this.state.chapters.length - 1);
                    }
                    this.refreshUI();
                    window.Modal.toast("챕터가 삭제되었습니다.");
                }
            },
            onReorder: (oldIdx, newIdx) => {
                const moved = this.state.chapters.splice(oldIdx, 1)[0];
                this.state.chapters.splice(newIdx, 0, moved);

                // Adjust Active Index
                if (this.activeIndex === oldIdx) {
                    this.activeIndex = newIdx;
                } else if (this.activeIndex > oldIdx && this.activeIndex <= newIdx) {
                    this.activeIndex--;
                } else if (this.activeIndex < oldIdx && this.activeIndex >= newIdx) {
                    this.activeIndex++;
                }
                this.refreshUI();
            },
            onUploadCover: async (blob) => {
                const safeName = `cover_${Date.now()}.webp`;
                return await MediaService.uploadImage(blob, `series/${this.seriesId}/${safeName}`);
            },
            onUpdate: () => this.triggerAutoSave() // Trigger auto-save on meta change
        });
        this.sidebar.render();

        // 2. Editor
        this.renderEditor();

        // 3. Preview
        this.renderPreview();
    }

    renderEditor() {
        const editorContainer = this.container.querySelector('#editor-area');
        this.editor = new WebnovelEditorArea(editorContainer, {
            chapter: this.state.chapters[this.activeIndex],
            onUpdate: (field) => {
                // If Title updated, need to update sidebar list item
                if (field === 'title') {
                    this.sidebar.renderChapterList();
                }
                // Trigger Auto Save
                this.triggerAutoSave();
                // [PERF] Manual Preview Only - Just Mark as Stale
                this.markPreviewStale();
            }
        });
        this.editor.render();
    }

    renderPreview() {
        const previewContainer = this.container.querySelector('#wn-preview-container');
        this.preview = new WebnovelPreview(previewContainer, {
            data: this.state,
            activeChapter: this.state.chapters[this.activeIndex]
        });
        this.preview.render();
    }

    refreshUI() {
        this.sidebar.props.activeIndex = this.activeIndex; // Update Props
        this.sidebar.renderChapterList(); // Re-render List
        this.renderEditor(); // Re-mount Editor
        this.renderEditor(); // Re-mount Editor
        this.renderPreview(); // Re-mount Preview
        this.markPreviewFresh(); // Reset Status
    }

    bindGlobalEvents() {
        this.container.querySelector('#btn-back').onclick = () => window.router.navigate('/studio');
        this.container.querySelector('#btn-refresh-preview').onclick = () => {
            this.renderPreview();
            this.markPreviewFresh();
        };

        // Workflow Buttons
        this.container.querySelector('#btn-save').onclick = () => this.save(true, false, null);
        this.container.querySelector('#btn-draft').onclick = () => this.save(true, false, 'draft');
        this.container.querySelector('#btn-publish').onclick = () => this.save(true, false, 'published');

        // [AI] Analysis Button
        const analyzeBtn = this.container.querySelector('#btn-analyze');
        if (analyzeBtn) analyzeBtn.onclick = () => this._handleAIAnalysis(analyzeBtn);
    }

    // [AI] Analysis Handler
    async _handleAIAnalysis(btn) {
        // Ensure state is saved first
        this.save(false, true, null);

        const currentChap = this.state.chapters[this.activeIndex];
        const content = currentChap.content;

        if (!content || content.length < 50) {
            window.Modal.toast("분석할 내용이 너무 짧습니다.");
            return;
        }

        try {
            const originalHTML = btn.innerHTML;
            btn.innerHTML = '<span class="material-symbols-outlined animate-spin text-sm">progress_activity</span>';
            btn.disabled = true;

            window.Modal.toast("AI가 내용을 분석하고 있습니다... (백그라운드)");

            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const { app } = await import("../../../firebase-config.js");
            const functions = getFunctions(app);

            // [FIX] Use Actual Document ID (UUID), NOT Index
            // Previous logic incorrectly used String(index) which created ghost documents (e.g. "1")
            const episodeId = currentChap.id;

            if (!episodeId) {
                throw new Error("Episode ID가 없습니다. 먼저 저장해주세요.");
            }

            const analyze = httpsCallable(functions, 'analyzeEpisodeText');
            await analyze({
                seriesId: this.seriesId,
                episodeId: episodeId,
                textContent: content
            });

            window.Modal.toast("AI 분석 완료! SEO 데이터가 생성되었습니다.");

        } catch (e) {
            console.error("AI Analysis Failed:", e);
            window.Modal.alert("AI 분석 실패: " + e.message);
        } finally {
            if (btn) {
                btn.innerHTML = '<span class="material-symbols-outlined text-sm">check_circle</span>';
                setTimeout(() => {
                    btn.innerHTML = '<span class="material-symbols-outlined text-sm">psychology</span> <span class="hidden md:inline">AI Analyze</span>';
                    btn.disabled = false;
                }, 2000);
            }
        }
    }

    triggerAutoSave() {
        if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = setTimeout(() => {
            console.log("[AutoSave] Triggering...");
            this.save(false, true, null); // Silent save
        }, 3000); // 3 seconds debounce
    }

    // [PERF] Optimized Preview Logic
    markPreviewStale() {
        const dot = this.container.querySelector('#preview-status-dot');
        const text = this.container.querySelector('#preview-status-text');
        if (dot) {
            dot.classList.remove('bg-green-500');
            dot.classList.add('bg-orange-500');
        }
        if (text) text.innerText = "업데이트 필요";
    }

    markPreviewFresh() {
        const dot = this.container.querySelector('#preview-status-dot');
        const text = this.container.querySelector('#preview-status-text');
        if (dot) {
            dot.classList.remove('bg-orange-500');
            dot.classList.add('bg-green-500');
        }
        if (text) text.innerText = "Live Preview";
    }

    async save(notify = false, silent = false, targetStatus = null) {
        if (targetStatus) {
            const activeChapter = this.state.chapters[this.activeIndex];
            if (activeChapter) {
                // [CHANGED] Logic for First-Time Approval vs Subsequent Auto-Publish
                const currentUser = window.user || {};
                const isAdmin = currentUser.role === 'admin' || currentUser.isSuperAdmin;
                const isSeriesPublished = this.state.status === 'published';

                // 1. If trying to Publish
                if (targetStatus === 'published') {
                    if (isSeriesPublished || isAdmin) {
                        // Already published OR Admin -> Allow immediate publish
                        activeChapter.status = 'published';
                        // Ensure Series is published (redundant but safe)
                        this.state.status = 'published';
                    } else {
                        // Not published yet AND Not Admin -> Force Pending
                        activeChapter.status = 'pending';
                        this.state.status = 'pending'; // Series also goes to pending

                        // Override targetStatus for UI feedback later
                        targetStatus = 'pending';
                    }
                } else {
                    // Draft or other status -> Force update
                    activeChapter.status = targetStatus;

                    // [FIX] If switching back to draft, we should allow it unconditionally
                    // If all chapters are draft, we 'could' revert series, but let's keep series state as is for now
                    // unless user specifically requested a series-level unpublish (which is a different button usually).
                    // For now, Editor 'Save Draft' targets the ACTIVE CHAPTER primarily.
                    // But if this is a 'Save Draft' action, we ensure the chapter is draft.
                    if (targetStatus === 'draft') {
                        console.log("Reverting Chapter to Draft");
                    }
                }
            }
        }

        const btn = this.container.querySelector('#btn-save');
        // prompt feedback immediately if explicit
        if (notify && btn) {
            // Keep original width to prevent jumping? simpler to just text
        }

        try {
            if (!silent) {
                const validation = WebnovelAdapter.validate(this.state);
                if (!validation.isValid) {
                    window.Modal.alert(validation.errors[0]);
                    return;
                }
            }

            // [FIX] Prepare Payload & Protect AuthorID
            const payload = WebnovelAdapter.toFirestore(this.state);

            // [MIGRATION] STOP Saving Chapters to Parent Doc
            delete payload.chapters;

            // CRITICAL: Protect Ownership (Admin Safeguard)
            const currentUser = window.user || {};
            const isAdmin = currentUser.role === 'admin' || currentUser.isSuperAdmin;

            if (isAdmin) {
                console.log("[Studio] Admin Save Mode: Protecting AuthorID");
                delete payload.authorId; // Never overwrite authorId as Admin
                delete payload.createdAt; // Preserve original creation time
                payload.lastUpdatedByAdmin = serverTimestamp();
            } else {
                payload.updatedAt = serverTimestamp();
            }

            // [FIX] Subcollection Sync Architecture (Hybrid: Array + Collections)
            const batch = writeBatch(db);
            const seriesRef = doc(db, "series", this.seriesId);

            // 1. Update Series Metadata (Parent)
            batch.update(seriesRef, payload);

            // 2. Sync Episodes to Subcollection
            // The Viewer reads 'series/{id}/episodes'. The Editor must write there.
            const episodesRef = collection(db, "series", this.seriesId, "episodes");

            this.state.chapters.forEach((ch, index) => {
                let epId = ch.id;
                let epRef;

                if (epId) {
                    epRef = doc(episodesRef, epId);
                } else {
                    epRef = doc(episodesRef); // Generate New ID
                    ch.id = epRef.id; // Assign back to state (important for subsequent saves)
                }

                const epData = {
                    seriesId: this.seriesId,
                    title: ch.title || `Episode ${index + 1}`,
                    content: ch.content || '',
                    note: ch.note || '',
                    bgmUrl: ch.bgmUrl || '',
                    status: ch.status || 'draft',
                    index: index + 1,
                    // If we are admin, we try NOT to touch authorId of episodes if possible,
                    // but 'set' needs it if new. 
                    // Fallback: If payload has authorId (from state), use it.
                    authorId: this.state.authorId || payload.authorId || currentUser.uid,
                    updatedAt: serverTimestamp()
                };

                // Admin Safety for Episodes
                if (isAdmin && this.state.authorId) {
                    epData.authorId = this.state.authorId;
                }

                batch.set(epRef, epData, { merge: true });
            });

            // 3. UI Feedback
            if (notify) {
                let loadMsg = "저장 중...";
                if (targetStatus === 'published') loadMsg = "발행 및 동기화 중...";
                else if (targetStatus === 'pending') loadMsg = "승인 요청 중...";
                window.Modal.toast(loadMsg);
            }

            // 4. Commit Transaction
            await batch.commit();

            // 5. Success UI
            if (!silent) {
                if (notify) {
                    let msg = "저장 완료";
                    if (targetStatus === 'published') {
                        // [VIRAL] Show Publish Success with Share Prompt (Episode-Specific)
                        const shareType = this.state.type === 'webtoon' ? 'webtoon' : (this.state.type === 'davinci' ? 'webtoon' : 'novel');
                        const epId = this.state.chapters[this.activeIndex]?.id || '';
                        const shareUrl = epId
                            ? `https://ssulwha.world/share/${shareType}/${this.seriesId}/${epId}`
                            : `https://ssulwha.world/share/${shareType}/${this.seriesId}`;
                        window.Modal.showPublishSuccess(shareUrl, this.state.title);
                    } else if (targetStatus === 'pending') {
                        // [FIX] Use Premium Approval Modal
                        window.Modal.showApprovalRequestSuccess();
                    } else if (targetStatus === 'draft') {
                        msg = "임시 저장 완료 (비공개 전환됨)";
                        window.Modal.toast(msg);
                    } else {
                        window.Modal.toast("저장 완료");
                    }
                }
                this.sidebar.renderChapterList();
            } else {
                const indicator = this.container.querySelector('#auto-save-indicator');
                if (indicator) {
                    indicator.style.opacity = '1';
                    setTimeout(() => indicator.style.opacity = '0', 2000);
                }
                console.log("[WebnovelStudio] Auto Saved (Synced)");
            }

        } catch (e) {
            console.error("Studio Save Error:", e);
            if (!silent) window.Modal.alert("저장 실패: " + e.message);
        }
    }
}
